/**
 * Date formatting utilities
 *
 * - Timestamps (client_close_datetime, etc.):
 *   stored as local Canberra time but without timezone suffix (treated as UTC by JS).
 *   We display as-is using 'UTC' to avoid double-applying the +11h offset.
 * - Date-only fields (YYYY-MM-DD, e.g. estimated_start_date):
 *   treated as calendar dates, displayed without timezone shift
 */

const CANBERRA_TZ = 'UTC';

/**
 * Convert a date string to a Date object in Canberra time for display.
 * Returns { date, isDateOnly } where isDateOnly means no time component was present.
 */
const parseDate = (dateString) => {
  const str = String(dateString).trim();
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(str);
  return { date: new Date(dateString), isDateOnly };
};

/**
 * Format a date or datetime string in Canberra time.
 * @param {string} dateString - ISO date or datetime string
 * @param {object} options - { style: 'short'|'long'|default, fallback: string }
 * @returns {string} Formatted date string in Canberra time
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return options.fallback || 'Not specified';

  try {
    const { date, isDateOnly } = parseDate(dateString);
    if (isNaN(date.getTime())) return options.fallback || 'Invalid date';

    // Date-only fields (YYYY-MM-DD) are calendar dates with no timezone component.
    // Use UTC so JavaScript's Date parsing doesn't shift the date.
    const tz = isDateOnly ? 'UTC' : CANBERRA_TZ;

    if (options.style === 'short') {
      return date.toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: tz,
      });
    } else if (options.style === 'long') {
      return date.toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: tz,
      });
    } else {
      return date.toLocaleDateString('en-AU', { timeZone: tz });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return options.fallback || 'Invalid date';
  }
};

/**
 * Format a datetime string showing date and time in Canberra time.
 * @param {string} dateString - ISO datetime string
 * @param {object} options - { style: 'short'|'long', showTime: boolean, fallback: string }
 * @returns {string} Formatted date+time string in Canberra time
 */
export const formatDateTime = (dateString, options = {}) => {
  if (!dateString) return options.fallback || 'Not specified';

  try {
    const { date, isDateOnly } = parseDate(dateString);
    if (isNaN(date.getTime())) return options.fallback || 'Invalid date';

    // If there's no time component, just format the date
    if (isDateOnly) return formatDate(dateString, options);

    const month = options.style === 'short' ? 'short' : 'long';

    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month,
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: CANBERRA_TZ,
      timeZoneName: 'short',
    });
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return options.fallback || 'Invalid date';
  }
};

/**
 * Get a YYYY-MM-DD date key in Canberra time from a datetime string.
 * Use this for grouping events by calendar date (e.g. in CalendarView).
 * @param {string} dateString - ISO datetime string
 * @returns {string} YYYY-MM-DD in Canberra time, or null
 */
export const toCanberraDateKey = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    // en-CA locale produces YYYY-MM-DD format
    return date.toLocaleDateString('en-CA', { timeZone: CANBERRA_TZ });
  } catch {
    return null;
  }
};
